import { getDataAPI, postDataAPI, putDataAPI, deleteDataAPI } from "../../../utils/fetchData";
import { setLoader, clearAlerts, setSuccessAlert, setErrorAlert } from "../../../store/alert/alertSlice";
import usageTracker from "../../../utils/usageTracker";
// Removed conversationContextManager - using direct conversation history like Cursor
import aiAppDataContext from "../../../utils/aiAppDataContext";

// Configuration for the OpenAI API
const API_URL = process.env.REACT_APP_API_URL || 'https://api.yourdomain.com';

// Helper function to convert file to base64 with smart compression
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    // For spreadsheet-like images, use minimal compression to preserve text readability
    const isLikelySpreadsheet = file.name.toLowerCase().includes('sheet') || 
                               file.name.toLowerCase().includes('table') ||
                               file.name.toLowerCase().includes('data') ||
                               file.name.toLowerCase().includes('screen') ||
                               file.name.toLowerCase().includes('shot') ||
                               file.name.toLowerCase().includes('spreadsheet') ||
                               file.name.toLowerCase().includes('excel') ||
                               file.name.toLowerCase().includes('csv') ||
                               file.name.toLowerCase().includes('shows') ||
                               file.name.toLowerCase().includes('performance') ||
                               file.size > 300000 || // Large images likely contain detailed data
                               file.type === 'image/png' || // PNG often used for screenshots
                               file.type === 'image/jpeg' && file.size > 200000; // Large JPEG likely has detailed data

    console.log(`🔍 Image analysis: ${file.name} (${file.type}, ${file.size} bytes)`);
    console.log(`📊 Spreadsheet detection: ${isLikelySpreadsheet ? 'YES - Using high quality' : 'NO - Using compression'}`);

    const MAX_BYTES = 3.5 * 1024 * 1024; // ~3.5MB

    if (isLikelySpreadsheet) {
      // If the file is massive (> ~6-8MB PNG screenshots), lightly downscale to keep under model limits
      if (file.size <= MAX_BYTES) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          console.log(`📊 HIGH-QUALITY MODE: Preserving original image quality for better text recognition`);
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
        return;
      }
      // Fall through to controlled downscale while keeping high quality
    }

    // Standard compression / controlled downscale
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      const maxSize = isLikelySpreadsheet ? 2048 : 1536; // allow a bit larger for spreadsheets
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else if (height > maxSize) {
        width = (width * maxSize) / height;
        height = maxSize;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const quality = isLikelySpreadsheet ? 0.96 : 0.92;
      const mime = 'image/jpeg';
      const dataUrl = canvas.toDataURL(mime, quality);
      const base64 = dataUrl.split(',')[1];
      console.log(`📸 Compressed: ${file.size} bytes → ~${Math.round((base64.length * 3) / 4)} bytes, ${width}x${height}`);
      resolve(base64);
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};



// Helper function to read CSV content as text
const csvToText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
  });
};

// Helper function to read PDF content as text
const pdfToText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // For now, we'll pass the PDF as base64 to the backend for processing
      // The backend will handle PDF text extraction
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Helper function to process files for AI
const processFileForAI = async (fileObj) => {
  console.log('Processing file object:', { 
    hasFile: !!fileObj.file, 
    name: fileObj.name, 
    type: fileObj.type,
    fileObjKeys: Object.keys(fileObj)
  });
  
  const { file, name, type, url } = fileObj;
  
  // If we already have a processed data URL (from auto-injection), return it directly
  if (url && url.startsWith('data:image/')) {
    console.log('🔄 Using already processed image:', { name, type, urlPreview: url.substring(0, 60) + '...' });
    return {
      type: 'image',
      url: url,
      name: name,
      fileType: type
    };
  }
  
  if (!file) {
    console.error('No file object found in:', fileObj);
    return null;
  }
  
  // Handle image files (new uploads)
  if (type.startsWith('image/')) {
    try {
      const base64 = await fileToBase64(file);
      return {
        type: 'image',
        url: `data:${type};base64,${base64}`,
        name: name,
        fileType: type
      };
    } catch (error) {
      console.error('Error processing image:', error);
      return null;
    }
  }
  
  // Handle PDF files - send directly to AI for processing
  if (type === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) {
    try {
      const pdfBase64 = await pdfToText(file);
      return {
        type: 'pdf',
        content: pdfBase64,
        name: name,
        fileType: type,
        // Mark as document for AI processing
        isDocument: true
      };
    } catch (error) {
      console.error('Error reading PDF:', error);
      return null;
    }
  }
  
  // Handle CSV files
  if (type === 'text/csv' || name.toLowerCase().endsWith('.csv')) {
    try {
      const csvContent = await csvToText(file);
      return {
        type: 'csv',
        content: csvContent,
        name: name,
        fileType: type
      };
    } catch (error) {
      console.error('Error reading CSV:', error);
      return null;
    }
  }
  
  // Handle other text files
  if (type.startsWith('text/') || name.toLowerCase().match(/\.(txt|json|xml|md)$/)) {
    try {
      const textContent = await csvToText(file); // Same function works for any text file
      return {
        type: 'text',
        content: textContent,
        name: name,
        fileType: type
      };
    } catch (error) {
      console.error('Error reading text file:', error);
      return null;
    }
  }
  
  console.warn('Unsupported file type:', type, name);
  return null;
};

// Function to process user questions with OpenAI
export const processQuestion = async (question, conversationHistory = [], dispatch) => {
  try {
    // Only use dispatch if it's provided
    if (dispatch) {
      dispatch(setLoader(true));
      dispatch(clearAlerts());
    }

    console.log("Sending question to backend:", question);
    console.log("Conversation history length:", conversationHistory.length);

    const response = await postDataAPI("user/chatWithAI", {
      prompt: question,
      conversation: conversationHistory,
      context: "music career management"
    });

    console.log("Response received from backend:", response);

    // Fix: Handle nested data structure - check both response.data.response and response.data.data.response
    if (response && response.data) {
      let responseData = response.data;
      let aiResponse = responseData.response;
      let conversation = responseData.conversation;

      // Check if data is nested one level deeper
      if (responseData.data && !aiResponse) {
        aiResponse = responseData.data.response;
        conversation = responseData.data.conversation;
      }

      if (aiResponse) {
        // Removed notification: if (dispatch) dispatch(setSuccessAlert("Response generated successfully"));
        
        // Track AI usage
        try {
          await usageTracker.trackAIRequest('gpt-4o-mini', question?.length || 0, aiResponse?.length || 0);
        } catch (trackingError) {
          console.error('Usage tracking failed:', trackingError);
        }
        
        return {
          success: true,
          response: aiResponse,
          conversation: conversation || conversationHistory
        };
      }
    }

    // If we got here, couldn't find response in the expected structure
    console.error("Invalid response format:", response);
    if (dispatch) dispatch(setErrorAlert("Received invalid response format from server"));
    return {
      success: false,
      error: "Received invalid response format from server"
    };
  } catch (error) {
    console.error("Error processing question with AI:", error);
    if (dispatch) dispatch(setErrorAlert(error?.response?.data?.message || "Error connecting to AI service"));
    return {
      success: false,
      error: error?.response?.data?.message || "Error connecting to AI service"
    };
  } finally {
    if (dispatch) dispatch(setLoader(false));
  }
};

// Function to get customized music career advice
export const getCareerAdvice = async (topic, specificDetails = {}, dispatch) => {
  try {
    const response = await postDataAPI("user/getCareerAdvice", {
      topic,
      details: specificDetails
    });

    if (response.status === 200) {
      return {
        success: true,
        advice: response.data?.advice,
        actionItems: response.data?.actionItems || []
      };
    } else {
      return {
        success: false,
        error: response.data?.message || "Failed to get career advice"
      };
    }
  } catch (error) {
    console.error("Error getting career advice:", error);
    return {
      success: false,
      error: error?.response?.data?.message || "Error connecting to AI service"
    };
  }
};



export const processQuestionForDizee = async (question, conversationHistory = [], dispatch, conversationId, images = []) => {
  try {
    // Only use dispatch if it's provided
    if (dispatch) {
      dispatch(setLoader(true));
      dispatch(clearAlerts());
    }

    // Simple approach: let the user ask naturally, AI will handle it
    let finalQuestion = question;
    const hasImages = images.some(img => img.type && img.type.startsWith('image/'));
    
    // Only add simple instruction if no text provided with image
    if (hasImages && (!question || question.trim().length === 0)) {
      finalQuestion = "Please extract all the data from this image. If it's a spreadsheet or table, get every row of data.";
      console.log("Applied simple extraction prompt");
    }

    console.log("Sending question to backend:", finalQuestion);
    console.log("Conversation history length:", conversationHistory.length);
    console.log("Images to send:", images.length);
    
    // 🔍 CRITICAL DEBUGGING: Track conversation ID being sent
    console.log("🆔 CONVERSATION ID BEING SENT TO API:", {
        conversationId: conversationId,
        conversationIdType: typeof conversationId,
        conversationIdLength: conversationId?.length,
        isEmptyString: conversationId === '',
        isNull: conversationId === null,
        isUndefined: conversationId === undefined,
        truthyValue: !!conversationId
    });
    
    let token = localStorage.getItem('dizeeToken');
    console.log(`dizeeToken::`, token);

    // CURSOR-STYLE: Use conversation history directly without complex processing
    console.log('📚 CURSOR-STYLE: Using conversation history directly');
    console.log('📝 Conversation history structure:', conversationHistory.map((msg, i) => ({
      index: i,
      type: msg.type,
      hasContent: !!msg.content,
      contentPreview: msg.content?.substring(0, 50) + '...'
    })));
    
    const finalConversationHistory = conversationHistory;

    // ✅ NEW: Get currently loaded app data for AI context (with error handling)
    let appDataContext = 'No app data currently loaded';
    let appDataStatus = {};
    
    try {
      appDataContext = aiAppDataContext.getFormattedContext();
      appDataStatus = aiAppDataContext.getDataStatus();
    } catch (appDataError) {
      console.error('Error getting app data context:', appDataError);
      appDataContext = 'Error loading app data';
    }

    console.log("🎯 CURSOR-STYLE: Conversation context:", {
      messageCount: finalConversationHistory.length,
      conversationId: conversationId,
      historyLength: finalConversationHistory.length,
      appDataAvailable: appDataStatus,
      filesCount: images.length
    });

    // Process all uploaded files (images, CSV, text files)
    console.log('Starting file processing for', images.length, 'files');
    const fileData = await Promise.all(
      images.map(async (fileObj, index) => {
        try {
          console.log(`Processing file ${index + 1}:`, fileObj.name);
          return await processFileForAI(fileObj);
        } catch (error) {
          console.error(`Error processing file ${index + 1} (${fileObj.name}):`, error);
          return null;
        }
      })
    );

    // Filter out null values and separate by type
    const validFiles = fileData.filter(file => file !== null);
    const processedImages = validFiles.filter(file => file.type === 'image');
    const processedTextFiles = validFiles.filter(file => ['csv', 'text'].includes(file.type));
    
    console.log("File processing results:", {
      totalFiles: images.length,
      validFiles: validFiles.length,
      processedImages: processedImages.length,
      processedTextFiles: processedTextFiles.length,
      textFileNames: processedTextFiles.map(f => f.name)
    });

    console.log('🚀 CURSOR-STYLE: Sending to API:', {
      prompt: finalQuestion ? finalQuestion.substring(0, 50) + '...' : 'No prompt',
      conversationId: conversationId,
      imagesCount: processedImages.length,
      filesCount: processedTextFiles.length,
      hasAppData: !!appDataContext
    });

    // Temporarily use simpler format for images to test
    const simpleImages = processedImages.map(img => ({
      url: img.url,
      name: img.name,
      type: img.fileType
    }));

    let response;
    try {
      // Temporarily use the most basic request format to test
      // 🔧 CRITICAL FIX: Always use the original conversationId parameter, not the enhanced context
      const finalConversationId = conversationId && conversationId.trim() !== '' ? conversationId : '';
      
      console.log('🆔 CURSOR-STYLE: Final conversation ID for API call:', {
        originalParam: conversationId,
        finalId: finalConversationId,
        willCreateNew: finalConversationId === ''
      });
      
      const basicRequest = {
        prompt: finalQuestion,
        conversation: finalConversationHistory,
        conversationId: finalConversationId,
        images: simpleImages
      };
      
      // Only add optional fields if they have data
      if (processedTextFiles.length > 0) {
        basicRequest.files = processedTextFiles;
      }
      if (appDataContext) {
        basicRequest.appDataContext = appDataContext;
      }
      
      console.log("🚀 CURSOR-STYLE: Final request payload:", {
        prompt: basicRequest.prompt?.substring(0, 100) + '...',
        conversationLength: basicRequest.conversation?.length || 0,
        conversationId: basicRequest.conversationId,
        images: basicRequest.images?.length > 0 ? `${basicRequest.images.length} images` : 'no images',
        files: basicRequest.files?.length > 0 ? `${basicRequest.files.length} files` : 'no files'
      });
      
      console.log("🗣️ CURSOR-STYLE: Conversation being sent to backend:", basicRequest.conversation);
      
      console.log("🚀 FRONTEND: Making API call to user/chatWithAIForDizee...");
      response = await postDataAPI("user/chatWithAIForDizee", basicRequest, token);
      console.log("✅ FRONTEND: API call completed successfully");
    } catch (apiError) {
      console.error("❌ FRONTEND: API Call failed:", {
        error: apiError.message,
        status: apiError.response?.status,
        statusText: apiError.response?.statusText,
        data: apiError.response?.data,
        code: apiError.code
      });
      
      if (dispatch) dispatch(setErrorAlert("Error connecting to AI service"));
      return {
        success: false,
        error: apiError.response?.data?.message || apiError.message || "Error connecting to AI service"
      };
    }

    console.log("📥 FRONTEND: Response received from backend:", response);
    console.log("📊 FRONTEND: Response status:", response.status);
    console.log("📋 FRONTEND: Response data structure:", response.data ? Object.keys(response.data) : 'No data');
    
    // Check for fake data in the response
    if (response?.data?.response && typeof response.data.response === 'string') {
      if (response.data.response.includes('The Weeknd') || response.data.response.includes('Show Schedule')) {
        console.log("🚨🚨🚨 FRONTEND: DETECTED FAKE DATA IN API RESPONSE! 🚨🚨🚨");
        console.log("🚨 FRONTEND: Response contains The Weeknd or Show Schedule");
        console.log("🚨 FRONTEND: Full response:", response.data.response);
      }
    }
    
    // Also check nested response structure
    if (response?.data?.data?.response && typeof response.data.data.response === 'string') {
      if (response.data.data.response.includes('The Weeknd') || response.data.data.response.includes('Show Schedule')) {
        console.log("🚨🚨🚨 FRONTEND: DETECTED FAKE DATA IN NESTED API RESPONSE! 🚨🚨🚨");
        console.log("🚨 FRONTEND: Nested response contains The Weeknd or Show Schedule");
        console.log("🚨 FRONTEND: Full nested response:", response.data.data.response);
      }
    }

    // Fix: Handle nested data structure - check both response.data.response and response.data.data.response
    if (response && response.data) {
      let responseData = response.data;
      let aiResponse = responseData.response;
      let conversation = responseData.conversation;

      // Check if data is nested one level deeper
      if (responseData.data && !aiResponse) {
        aiResponse = responseData.data.response;
        conversation = responseData.data.conversation;
        conversationId = responseData.data.conversationId;
        responseData.processedImages = responseData.data.processedImages;
      }

      if (aiResponse) {
        console.log('📝 FRONTEND: Processing AI response...');
        console.log('📝 FRONTEND: AI response preview:', aiResponse.substring(0, 200));
        
        // Final check for fake data before returning to UI
        if (aiResponse.includes('The Weeknd') || aiResponse.includes('Show Schedule')) {
          console.log("🚨🚨🚨 FRONTEND: FINAL CHECK - FAKE DATA DETECTED! 🚨🚨🚨");
          console.log("🚨 FRONTEND: About to display fake data to user!");
          console.log("🚨 FRONTEND: Full AI response:", aiResponse);
        }
        
        // Removed notification: if (dispatch) dispatch(setSuccessAlert("Response generated successfully"));
        
        // CURSOR-STYLE: No need for complex context management - conversation history is handled directly
        console.log('✅ CURSOR-STYLE: Response processed successfully');
        
        // Track AI usage
        try {
          await usageTracker.trackAIRequest('gpt-4o-mini', question?.length || 0, aiResponse?.length || 0);
        } catch (trackingError) {
          console.error('Usage tracking failed:', trackingError);
        }
        
        console.log(`🔍 Backend response analysis:`, {
          hasProcessedImages: !!(responseData.processedImages && responseData.processedImages.length > 0),
          processedImagesCount: responseData.processedImages?.length || 0,
          processedImageSample: responseData.processedImages?.[0] ? {
            name: responseData.processedImages[0].name,
            urlType: responseData.processedImages[0].url?.startsWith('data:image/') ? 'VALID-data-url' : 'INVALID',
            urlPreview: responseData.processedImages[0].url?.substring(0, 60) + '...'
          } : 'No images'
        });

        return {
          success: true,
          response: aiResponse,
          conversation: conversation || conversationHistory,
          conversationId,
          processedImages: responseData.processedImages || []
        };
      }
    }

    // If we got here, couldn't find response in the expected structure
    console.error("Invalid response format:", response);
    if (dispatch) dispatch(setErrorAlert("Received invalid response format from server"));
    return {
      success: false,
      error: "Received invalid response format from server"
    };
  } catch (error) {
    console.error("Error processing question with AI:", error);
    if (dispatch) dispatch(setErrorAlert(error?.response?.data?.message || "Error connecting to AI service"));
    return {
      success: false,
      error: error?.response?.data?.message || "Error connecting to AI service"
    };
  } finally {
    if (dispatch) dispatch(setLoader(false));
  }
};



export const getConversationHistoryForDizee = async () => {
  try {
    let token = localStorage.getItem('dizeeToken');
    console.log('🔑 Token check:', {
      tokenExists: !!token,
      tokenLength: token?.length,
      tokenPrefix: token?.substring(0, 10) + '...',
    });
    
    if (!token) {
      console.error('❌ No token found in localStorage');
      return {
        success: false,
        error: "No authentication token found"
      };
    }

    console.log('🌐 Making API request to: user/getAIChatHistory');
    const response = await getDataAPI(`user/getAIChatHistory`, token);
    console.log(`📡 getConversationHistoryForDizee API response:`, {
      status: response?.status,
      hasData: !!response?.data,
      dataKeys: Object.keys(response?.data || {}),
      fullResponse: response
    });
    
    if (response.status === 200) {
      const conversations = response.data?.data?.conversations || [];
      console.log('✅ Successfully parsed conversations:', {
        count: conversations.length,
        firstConversation: conversations[0],
        conversationTitles: conversations.map(c => c.title),
        conversationIds: conversations.map(c => c._id),
        conversationDates: conversations.map(c => c.createdAt),
        // Check for missing required fields
        missingTitles: conversations.filter(c => !c.title).length,
        missingIds: conversations.filter(c => !c._id).length,
        missingDates: conversations.filter(c => !c.createdAt).length
      });
      
      return {
        success: true,
        conversations: conversations
      };
    } else {
      console.error('❌ API returned non-200 status:', response.status, response.data);
      return {
        success: false,
        error: response.data?.message || "Failed to get conversation history"
      };
    }
  } catch (error) {
    console.error("💥 Error fetching conversation history:", {
      message: error.message,
      response: error?.response?.data,
      status: error?.response?.status,
      fullError: error
    });
    return {
      success: false,
      error: error?.response?.data?.message || "Error connecting to database"
    };
  }
};

export const renameConversation = async (conversationId, newTitle) => {
  try {
    let token = localStorage.getItem('dizeeToken');
    console.log('✏️ Renaming conversation:', conversationId, 'to:', newTitle);
    
    const response = await putDataAPI(`user/conversations/${conversationId}/rename`, {
      title: newTitle
    }, token);
    
    console.log('📡 Rename conversation API response:', response);
    
    if (response.status === 200) {
      return {
        success: true,
        conversation: response.data?.data?.conversation
      };
    } else {
      return {
        success: false,
        error: response.data?.message || "Failed to rename conversation"
      };
    }
  } catch (error) {
    console.error("💥 Error renaming conversation:", error);
    return {
      success: false,
      error: error?.response?.data?.message || "Error connecting to server"
    };
  }
};

export const deleteConversation = async (conversationId) => {
  try {
    let token = localStorage.getItem('dizeeToken');
    console.log('🗑️ Deleting conversation:', conversationId);
    
    const response = await deleteDataAPI(`user/conversations/${conversationId}`, token);
    
    console.log('📡 Delete conversation API response:', response);
    
    if (response.status === 200) {
      return {
        success: true,
        deletedConversation: response.data?.data?.deletedConversation,
        deletedMessages: response.data?.data?.deletedMessages
      };
    } else {
      return {
        success: false,
        error: response.data?.message || "Failed to delete conversation"
      };
    }
  } catch (error) {
    console.error("💥 Error deleting conversation:", error);
    return {
      success: false,
      error: error?.response?.data?.message || "Error connecting to server"
    };
  }
};